/**
 * Les trois états que tout écran partage : chargement, erreur, données.
 *
 * Sans ce fichier, cinq écrans écriraient cinq fois le même `useEffect` avec
 * cinq façons différentes d'oublier l'annulation au démontage. Ici, elle est
 * faite une fois.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import type { DependencyList } from "react";
import { isAbortedError } from "./errors";
import { newIdempotencyKey } from "./idempotency";

/** État d'une lecture. */
export type QueryState<T> = {
  /** La charge utile, ou `null` tant qu'on n'a rien reçu. */
  data: T | null;
  /** Vrai pendant le premier chargement **et** pendant un rechargement. */
  loading: boolean;
  /** L'erreur du dernier essai, ou `null`. Voir `errorMessage(err)`. */
  error: unknown;
  /** Relance l'appel — à brancher sur un bouton « Réessayer ». */
  reload: () => void;
};

/**
 * Lit l'API et suit son état.
 *
 * ```tsx
 * const etat = useApiQuery((o) => api.getWalletState(walletId, o), [walletId]);
 * ```
 *
 * Le `fetcher` reçoit les options à repasser au client : il **doit** les
 * transmettre, c'est ce qui annule la requête au démontage. Le tableau `deps`
 * joue le rôle de celui d'un `useEffect` : l'appel est rejoué quand il change.
 */
export function useApiQuery<T>(
  fetcher: (opts: { signal: AbortSignal }) => Promise<T>,
  deps: DependencyList,
): QueryState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, relancer] = useReducer((n: number) => n + 1, 0);

  // Le fetcher est presque toujours une lambda recréée à chaque rendu : le
  // garder dans une ref évite de faire de son identité une dépendance, ce qui
  // relancerait l'appel en boucle. Les vraies dépendances sont dans `deps`.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    const controleur = new AbortController();
    let vivant = true;
    setLoading(true);

    fetcherRef
      .current({ signal: controleur.signal })
      .then((recu) => {
        if (!vivant) return;
        setData(recu);
        setError(null);
      })
      .catch((cause: unknown) => {
        // Une annulation n'est pas une erreur à montrer : elle vient de nous.
        if (!vivant || isAbortedError(cause)) return;
        setError(cause);
      })
      .finally(() => {
        if (vivant) setLoading(false);
      });

    return () => {
      vivant = false;
      controleur.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `deps` est fourni par l'appelant, exactement comme pour useEffect.
  }, [nonce, ...deps]);

  const reload = useCallback(() => relancer(), []);
  return { data, loading, error, reload };
}

/** État d'une écriture. */
export type MutationState<A extends unknown[], T> = {
  /**
   * Lance l'écriture. Rend la charge utile, ou `null` si elle a échoué —
   * l'erreur est alors dans `error`. Ne lève jamais : un `await` non protégé
   * dans un gestionnaire d'événement est la première source d'écran blanc.
   */
  run: (...args: A) => Promise<T | null>;
  /** Le dernier résultat obtenu, ou `null`. */
  data: T | null;
  /** L'erreur du dernier essai, ou `null`. Voir `insufficientFunds(err)`. */
  error: unknown;
  /** Vrai pendant l'envoi — à brancher sur `loading` et `disabled` du bouton. */
  pending: boolean;
  /** Efface résultat et erreur, sans rien renvoyer au serveur. */
  reset: () => void;
};

/**
 * Écrit dans l'API et suit son état.
 *
 * ```tsx
 * const achat = useApiMutation(api.creditPack);
 * const { key } = useIdempotencyKey([packChoisi]);
 * …
 * const recu = await achat.run(walletId, { pack_id: packChoisi }, { idempotencyKey: key });
 * if (!recu) return; // achat.error porte le refus
 * ```
 */
export function useApiMutation<A extends unknown[], T>(
  action: (...args: A) => Promise<T>,
): MutationState<A, T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState(false);

  const actionRef = useRef(action);
  actionRef.current = action;

  // Un composant démonté pendant l'envoi ne doit plus être mis à jour ; l'appel
  // lui-même, en revanche, n'est pas annulé : une écriture partie doit aller au
  // bout côté serveur.
  const monte = useRef(true);
  useEffect(() => {
    monte.current = true;
    return () => {
      monte.current = false;
    };
  }, []);

  // Piège à connaître : `run` rend `null` en échec et pose l'erreur dans l'état
  // React. Lire `mutation.error` juste après le `await`, dans la même fermeture,
  // rend la valeur du rendu PRÉCÉDENT — donc `null` au premier refus. C'est la
  // sémantique normale des fermetures React, pas un défaut d'ici, mais le motif
  // est tentant et faux. Trois usages corrects : tester la valeur rendue
  // (`if (recu === null)`), réagir dans un `useEffect` sur `mutation.error`, ou
  // lire l'erreur au rendu suivant pour l'afficher.
  const run = useCallback(async (...args: A): Promise<T | null> => {
    setPending(true);
    setError(null);
    try {
      const recu = await actionRef.current(...args);
      if (monte.current) {
        setData(recu);
      }
      return recu;
    } catch (cause: unknown) {
      if (monte.current) {
        setError(cause);
      }
      return null;
    } finally {
      if (monte.current) {
        setPending(false);
      }
    }
  }, []);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  return { run, data, error, pending, reset };
}

/**
 * Une clé d'idempotence stable pour une intention.
 *
 * La clé est fabriquée une fois, puis réutilisée à chaque tentative : c'est ce
 * qui fait qu'un double clic, ou un renvoi après une coupure, ne crédite pas
 * deux fois. Elle se renouvelle quand `deps` change (l'utilisateur a choisi un
 * autre pack : c'est une autre intention) ou sur appel explicite à `renew()`,
 * après un succès dont on veut pouvoir refaire un second exemplaire.
 */
export function useIdempotencyKey(deps: DependencyList = []): {
  key: string;
  renew: () => void;
} {
  const [nonce, renouveler] = useReducer((n: number) => n + 1, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `deps` est fourni par l'appelant, comme pour useMemo.
  const key = useMemo(() => newIdempotencyKey(), [nonce, ...deps]);
  const renew = useCallback(() => renouveler(), []);
  return { key, renew };
}
